import { useCallback, useEffect, useState } from "react";
import { Coins, Download, Undo2, XCircle } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { TooltipButton } from "@/components/common/Tooltips";
import { StatusBadge } from "@/components/common/StatusBadge";
import { api, apiError } from "@/lib/api";
import { useFactorajeHabilitado } from "@/hooks/useFactorajeHabilitado";
import { fdate, mxn } from "@/lib/format";
import { toast } from "sonner";
import { DevolucionRemanenteDialog } from "./DevolucionRemanenteDialog";
import { CancelarDevolucionDialog } from "./CancelarDevolucionDialog";

export const descargarAcuseDevolucion = async (id) => {
  try {
    const r = await api.get(`/factoraje/devoluciones/${id}/acuse-pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) {
    toast.error(apiError(e));
  }
};

function DevolucionesRegistradas({ devs, puedeCancelar, onCancelar }) {
  if (!devs.length) return null;
  return (
    <div className="border-t border-slate-100" data-testid="devoluciones-registradas">
      <p className="px-4 pt-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Devoluciones registradas</p>
      <Table>
        <TableHeader><TableRow>
          <TableHead>Folio</TableHead><TableHead>Crédito</TableHead><TableHead>Fecha</TableHead>
          <TableHead className="text-right">Monto</TableHead><TableHead>Medio · cuenta</TableHead>
          <TableHead>Estatus</TableHead><TableHead className="text-right">Acción</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {devs.map((d) => (
            <TableRow key={d.id} data-testid={`devolucion-row-${d.id}`} className={d.estatus !== "Vigente" ? "opacity-60" : ""}>
              <TableCell className="font-mono text-xs">{d.folio}</TableCell>
              <TableCell>{d.numero_credito}</TableCell>
              <TableCell>{fdate(d.fecha)}</TableCell>
              <TableCell className="text-right tabular-nums">{mxn(d.monto)}</TableCell>
              <TableCell className="text-xs">{d.medio} · {d.cuenta}{d.cuenta_distinta ? " (distinta)" : ""}</TableCell>
              <TableCell><StatusBadge value={d.estatus} /></TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-1.5">
                  <TooltipButton tooltip="Descargar el acuse de devolución (PDF)" size="sm" variant="outline"
                    data-testid={`devolucion-acuse-${d.id}`} onClick={() => descargarAcuseDevolucion(d.id)}>
                    <Download className="mr-1 h-3.5 w-3.5" /> Acuse
                  </TooltipButton>
                  {puedeCancelar && d.estatus === "Vigente" && (
                    <TooltipButton tooltip="Cancelar la devolución (el remanente vuelve a quedar pendiente)" size="sm" variant="outline"
                      data-testid={`devolucion-cancelar-${d.id}`} onClick={() => onCancelar(d)}>
                      <XCircle className="mr-1 h-3.5 w-3.5 text-rose-600" /> Cancelar
                    </TooltipButton>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// Build 3 · Mesa de Control › Remanentes por devolver (solo entidades con factoraje).
export function RemanentesPorDevolver() {
  const habilitado = useFactorajeHabilitado();
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(null);
  const [cancelar, setCancelar] = useState(null);
  const load = useCallback(() => {
    api.get("/factoraje/remanentes").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);
  useEffect(() => { if (habilitado) load(); }, [habilitado, load]);
  if (!habilitado || !data) return null;
  const filas = data.remanentes || [];
  return (
    <div className="rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="remanentes-bandeja">
      <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-3">
        <Coins className="h-4 w-4 text-forest-600" />
        <h3 className="text-sm font-semibold text-slate-700">Remanentes por devolver</h3>
        <span className="ml-auto text-xs text-slate-500" data-testid="remanentes-total">
          {filas.length} operación(es) · {mxn(filas.reduce((a, f) => a + Number(f.pendiente || 0), 0))}
        </span>
      </div>
      {filas.length === 0 ? (
        <p className="py-8 text-center text-sm text-slate-400" data-testid="remanentes-vacio">No hay remanentes pendientes de devolver.</p>
      ) : (
        <Table>
          <TableHeader><TableRow>
            <TableHead>Crédito</TableHead><TableHead>Cliente</TableHead><TableHead>Deudor</TableHead>
            <TableHead className="text-right">Remanente pendiente</TableHead><TableHead>Fecha del cobro</TableHead>
            <TableHead className="text-right">Días pendientes</TableHead><TableHead className="text-right">Acción</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {filas.map((f) => (
              <TableRow key={f.credito_id} data-testid={`remanente-row-${f.credito_id}`}>
                <TableCell className="font-medium">{f.numero_credito}</TableCell>
                <TableCell>{f.cliente}</TableCell>
                <TableCell className="text-xs text-slate-600">{f.deudor || "—"}</TableCell>
                <TableCell className="text-right font-semibold tabular-nums" data-testid={`remanente-pendiente-${f.credito_id}`}>{mxn(f.pendiente)}</TableCell>
                <TableCell>{fdate(f.fecha_cobro)}</TableCell>
                <TableCell className="text-right tabular-nums">{f.dias_pendientes ?? "—"}</TableCell>
                <TableCell className="text-right">
                  {data.puede_registrar && (
                    <TooltipButton tooltip="Registra la devolución del remanente al cliente" size="sm"
                      data-testid={`devolver-btn-${f.credito_id}`} onClick={() => setSel(f)}>
                      <Undo2 className="mr-1.5 h-4 w-4" /> Devolver
                    </TooltipButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <DevolucionesRegistradas devs={data.devoluciones || []} puedeCancelar={data.puede_cancelar} onCancelar={setCancelar} />
      <DevolucionRemanenteDialog fila={sel} onClose={() => setSel(null)} onAcuse={descargarAcuseDevolucion}
        onDone={() => { setSel(null); load(); }} />
      <CancelarDevolucionDialog dev={cancelar} onClose={() => setCancelar(null)} onDone={() => { setCancelar(null); load(); }} />
    </div>
  );
}
